import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import React from "react";
import {
  CheckCircleIcon,
  SearchIcon,
  WarningIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { MdLocationOn, MdEvStation } from "react-icons/md";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import HL_ProfilePricing from "./headless/HL_ProfilePricing";

// Motion components
const MotionBox = motion(Box);

// Glass Morphism Card
const GlassMorphismCard = styled(Box)`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  border: 1px solid rgba(245, 125, 54, 0.15);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

/**
 * ProfileStationsPricing - UI Component
 * Uses headless component (HL_ProfilePricing) for all business logic
 * Focuses purely on presentation and user interaction
 */
const ProfileStationsPricing = ({ id, data, onRefresh }) => {
  // Theme values must be called at component level, not inside render props
  const themeColor = "#f57d36";
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.800");

  // Global pricing application state
  const [showGlobalPricing, setShowGlobalPricing] = React.useState(false);
  const [applicationType, setApplicationType] = React.useState("percentage"); // "percentage" or "fixed"
  const [reductionValue, setReductionValue] = React.useState("");

  // Apply global pricing to all stations
  const applyGlobalPricing = (
    actualData,
    handlePriceChange,
    toggleCustomPricing,
    getStationState
  ) => {
    const value = parseFloat(reductionValue);
    if (isNaN(value) || value < 0) return;

    actualData.forEach((location) => {
      location.Stations.forEach((station) => {
        const globalPrice = station.PricePerUnit;
        let newPrice;

        if (applicationType === "percentage") {
          // Reduce by percentage
          newPrice = globalPrice - (globalPrice * value) / 100;
        } else {
          // Reduce by fixed amount
          newPrice = globalPrice - value;
        }

        // Ensure price doesn't go below zero
        newPrice = Math.max(0, newPrice);

        // Check if station already has custom pricing
        const state = getStationState(location.Id, station.Id);

        // If not enabled, enable it first
        if (state.status === "none") {
          toggleCustomPricing(location.Id, station.Id, globalPrice);
        }

        // Set the new price
        handlePriceChange(location.Id, station.Id, newPrice.toFixed(2));
      });
    });

    // Reset form
    setReductionValue("");
    setShowGlobalPricing(false);
  };

  // Render function that receives props from headless component
  const renderUI = ({
    actualData,
    filteredLocations,
    searchQuery,
    setSearchQuery,
    getStationState,
    isInUpdatedState,
    isInDeletedState,
    editMode,
    toggleEditMode,
    handlePriceChange,
    toggleCustomPricing,
    getDisplayPrice,
    handleSaveChanges,
    handleDiscardChanges,
    hasUnsavedChanges,
    updateLoading,
    updatedCount,
    deletedCount,
    expandedItems,
    setExpandedItems,
    expandAll,
    collapseAll,
  }) => {
    return (
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
      >
        {/* Header */}
        <GlassMorphismCard p={6} mb={6}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Flex align="center" gap={2}>
                <Text fontSize="2xl" fontWeight="bold" color={themeColor}>
                  ₹
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Station Pricing Management
                </Text>
              </Flex>
              <Text fontSize="xs" color="gray.500">
                Customize pricing for individual stations or use default pricing
              </Text>
            </VStack>

            {/* Search */}
            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search stations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                focusBorderColor={themeColor}
                borderRadius="md"
                size="sm"
              />
            </InputGroup>
          </Flex>

          {/* Global Pricing Application */}
          <Box mt={4} pt={4} borderTop="1px solid" borderColor={borderColor}>
            <Flex align="center" gap={2} mb={2}>
              <Switch
                colorScheme="orange"
                isChecked={showGlobalPricing}
                onChange={(e) => setShowGlobalPricing(e.target.checked)}
                size="sm"
              />
              <Text fontSize="sm" fontWeight="600" color={textColor}>
                Toggle to handle pricing for all the chargers at once
              </Text>
            </Flex>

            <Text
              fontSize="xs"
              color="gray.500"
              fontStyle="italic"
              mb={3}
              ml={10}
            >
              Note: Reduced tariffs will always be calculated against default
              public price only. To reduce existing custom tariffs, please edit
              them manually.
            </Text>

            {showGlobalPricing && (
              <Flex gap={2} flexWrap="wrap" align="end">
                {/* Application Type */}
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Reduction Type
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant={
                        applicationType === "percentage" ? "solid" : "outline"
                      }
                      colorScheme="orange"
                      onClick={() => setApplicationType("percentage")}
                      fontSize="xs"
                      px={3}
                      h="32px"
                    >
                      Percentage (%)
                    </Button>
                    <Button
                      size="sm"
                      variant={
                        applicationType === "fixed" ? "solid" : "outline"
                      }
                      colorScheme="orange"
                      onClick={() => setApplicationType("fixed")}
                      fontSize="xs"
                      px={3}
                      h="32px"
                    >
                      Fixed (₹)
                    </Button>
                  </HStack>
                </Box>

                {/* Reduction Value */}
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Reduction Value
                  </Text>
                  <NumberInput
                    size="sm"
                    min={0}
                    max={applicationType === "percentage" ? 100 : undefined}
                    value={reductionValue}
                    onChange={(value) => setReductionValue(value)}
                    w="120px"
                  >
                    <NumberInputField
                      placeholder={
                        applicationType === "percentage" ? "0-100" : "Amount"
                      }
                      fontSize="sm"
                      h="32px"
                    />
                  </NumberInput>
                </Box>

                {/* Apply Button */}
                <Button
                  size="sm"
                  bg={themeColor}
                  color="white"
                  onClick={() =>
                    applyGlobalPricing(
                      actualData,
                      handlePriceChange,
                      toggleCustomPricing,
                      getStationState
                    )
                  }
                  isDisabled={!reductionValue || parseFloat(reductionValue) < 0}
                  fontSize="xs"
                  px={4}
                  h="32px"
                  _hover={{
                    bg: "rgba(245, 125, 54, 0.9)",
                  }}
                >
                  Apply to All
                </Button>
              </Flex>
            )}
          </Box>

          {/* Action Buttons */}
          {hasUnsavedChanges && (
            <Flex mt={3} gap={2} align="center">
              <Button
                size="sm"
                bg={themeColor}
                color="white"
                colorScheme="orange"
                onClick={handleSaveChanges}
                isLoading={updateLoading}
                fontSize="xs"
                px={4}
                h="32px"
                _hover={{
                  bg: "rgba(245, 125, 54, 0.9)",
                }}
              >
                Save Changes
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDiscardChanges}
                borderColor={themeColor}
                color={themeColor}
                fontSize="xs"
                px={4}
                h="32px"
                _hover={{
                  bg: "rgba(245, 125, 54, 0.1)",
                }}
              >
                Discard
              </Button>
              <Badge
                colorScheme="orange"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="md"
                fontWeight="600"
              >
                {updatedCount + deletedCount} changes
              </Badge>
            </Flex>
          )}
        </GlassMorphismCard>

        {/* Locations Accordion */}
        <GlassMorphismCard p={0}>
          {/* Expand/Collapse Controls */}
          <Flex
            justify="space-between"
            align="center"
            px={6}
            py={3}
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" fontWeight="600" color={textColor}>
              Locations ({filteredLocations.length})
            </Text>
            <HStack spacing={2}>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="orange"
                onClick={expandAll}
                fontSize="xs"
                h="24px"
                px={2}
              >
                Expand All
              </Button>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="orange"
                onClick={collapseAll}
                fontSize="xs"
                h="24px"
                px={2}
              >
                Collapse All
              </Button>
            </HStack>
          </Flex>

          <Accordion
            allowMultiple
            index={expandedItems}
            onChange={setExpandedItems}
          >
            {filteredLocations.map((location, index) => {
              const locationHasCustomPricing = location.Stations.some(
                (station) => {
                  const state = getStationState(location.Id, station.Id);
                  return state.status !== "none" && state.status !== "deleted";
                }
              );

              return (
                <AccordionItem
                  key={location.Id}
                  border="none"
                  borderBottom="2px solid"
                  borderColor="rgba(245, 125, 54, 0.2)"
                  bg={
                    index % 2 === 0 ? "transparent" : "rgba(245, 125, 54, 0.02)"
                  }
                >
                  <AccordionButton
                    py={4}
                    px={6}
                    _hover={{ bg: "rgba(245, 125, 54, 0.08)" }}
                  >
                    <Flex flex="1" align="center" gap={3}>
                      <Icon as={MdLocationOn} boxSize={5} color={themeColor} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="600" color={textColor}>
                          {location.Name}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {location.Address}, {location.City}
                        </Text>
                      </VStack>
                      <Badge
                        ml={2}
                        colorScheme="blue"
                        fontSize="xs"
                        borderRadius="full"
                      >
                        {location.Stations.length} station(s)
                      </Badge>
                      {locationHasCustomPricing && (
                        <Badge
                          colorScheme="orange"
                          fontSize="xs"
                          borderRadius="full"
                        >
                          Custom Pricing
                        </Badge>
                      )}
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel p={0}>
                    <Box 
                      overflowX="auto"
                      sx={{
                        '&::-webkit-scrollbar': {
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(245, 125, 54, 0.05)',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(245, 125, 54, 0.3)',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'rgba(245, 125, 54, 0.5)',
                        },
                      }}
                    >
                      <Table
                        variant="simple"
                        size="sm"
                        style={{ tableLayout: "auto", minWidth: "900px" }}
                      >
                        <Thead bg={tableHeaderBg}>
                          <Tr>
                            <Th minW="150px">Station</Th>
                            <Th minW="120px">EVSE ID</Th>
                            <Th minW="80px">Type</Th>
                            <Th minW="100px">Power (kW)</Th>
                            <Th minW="140px">Default Price (₹/kWh)</Th>
                            <Th minW="140px">Custom Price (₹/kWh)</Th>
                            <Th minW="100px">Status</Th>
                            <Th minW="120px">Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {location.Stations.map((station, stationIndex) => {
                            const key = `${location.Id}-${station.Id}`;
                            const stationState = getStationState(
                              location.Id,
                              station.Id
                            );

                            // Determine if custom pricing is active
                            const isCustom =
                              stationState.status !== "none" &&
                              stationState.status !== "deleted";

                            // Check if there's a pending change
                            const hasChange =
                              isInUpdatedState(location.Id, station.Id) ||
                              isInDeletedState(location.Id, station.Id);

                            const isEditing = editMode[key];
                            const displayPrice = getDisplayPrice(
                              location.Id,
                              station.Id,
                              station.PricePerUnit
                            );

                            // Determine background color
                            const bgColor = hasChange
                              ? "orange.50"
                              : stationIndex % 2 === 0
                              ? "transparent"
                              : "rgba(0, 0, 0, 0.02)";

                            return (
                              <Tr
                                key={station.Id}
                                _hover={{ bg: "rgba(245, 125, 54, 0.05)" }}
                                bg={bgColor}
                                borderBottom="1px solid"
                                borderColor={borderColor}
                              >
                                <Td>
                                  <Flex align="center" gap={2}>
                                    <Icon
                                      as={MdEvStation}
                                      color={themeColor}
                                      boxSize={4}
                                    />
                                    <Text fontWeight="500">{station.Name}</Text>
                                  </Flex>
                                </Td>
                                <Td>
                                  <Text
                                    fontSize="sm"
                                    fontFamily="mono"
                                    color="gray.600"
                                  >
                                    {station.EvseId}
                                  </Text>
                                </Td>
                                <Td>
                                  <Badge colorScheme="purple" fontSize="xs">
                                    {station.Type}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Text fontWeight="500">{station.Power}</Text>
                                </Td>
                                <Td>
                                  <Text fontWeight="600" color="gray.600">
                                    ₹{station.PricePerUnit.toFixed(2)}
                                  </Text>
                                </Td>
                                <Td>
                                  {isEditing ? (
                                    <NumberInput
                                      size="sm"
                                      min={0}
                                      step={0.1}
                                      precision={2}
                                      value={displayPrice}
                                      onChange={(value) =>
                                        handlePriceChange(
                                          location.Id,
                                          station.Id,
                                          value
                                        )
                                      }
                                      maxW="120px"
                                    >
                                      <NumberInputField
                                        focusBorderColor={themeColor}
                                        borderColor={themeColor}
                                        borderWidth="2px"
                                        bg="white"
                                        fontWeight="600"
                                        color={themeColor}
                                        _hover={{
                                          borderColor: themeColor,
                                          boxShadow: `0 0 0 1px ${themeColor}`,
                                        }}
                                        _focus={{
                                          borderColor: themeColor,
                                          boxShadow: `0 0 0 3px rgba(245, 125, 54, 0.1)`,
                                        }}
                                      />
                                    </NumberInput>
                                  ) : (
                                    <Text
                                      fontWeight="600"
                                      color={isCustom ? themeColor : "gray.400"}
                                    >
                                      {isCustom
                                        ? `₹${displayPrice.toFixed(2)}`
                                        : "-"}
                                    </Text>
                                  )}
                                </Td>
                                <Td>
                                  {isCustom ? (
                                    <Badge
                                      colorScheme="green"
                                      fontSize="xs"
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      w="fit-content"
                                    >
                                      <CheckCircleIcon boxSize={3} />
                                      Custom
                                    </Badge>
                                  ) : (
                                    <Badge
                                      colorScheme="gray"
                                      fontSize="xs"
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      w="fit-content"
                                    >
                                      Default
                                    </Badge>
                                  )}
                                </Td>
                                <Td>
                                  <HStack spacing={2}>
                                    {isCustom && (
                                      <Tooltip
                                        label={
                                          isEditing
                                            ? "Save Price"
                                            : "Edit Price"
                                        }
                                      >
                                        <IconButton
                                          icon={<EditIcon />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme={
                                            isEditing ? "green" : "blue"
                                          }
                                          onClick={() =>
                                            toggleEditMode(
                                              location.Id,
                                              station.Id
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                    <Tooltip
                                      label={
                                        isCustom
                                          ? "Use Default Pricing"
                                          : "Enable Custom Pricing"
                                      }
                                      closeOnClick
                                    >
                                      <Box>
                                        <Switch
                                          colorScheme="orange"
                                          isChecked={isCustom}
                                          onChange={() =>
                                            toggleCustomPricing(
                                              location.Id,
                                              station.Id,
                                              station.PricePerUnit
                                            )
                                          }
                                        />
                                      </Box>
                                    </Tooltip>
                                  </HStack>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>

          {filteredLocations.length === 0 && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={12}
              gap={3}
            >
              <Icon as={WarningIcon} boxSize={12} color="gray.400" />
              <Text fontSize="lg" color="gray.500" fontWeight="500">
                No stations found
              </Text>
              <Text fontSize="sm" color="gray.400">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "No stations available for this profile"}
              </Text>
            </Flex>
          )}
        </GlassMorphismCard>
      </MotionBox>
    );
  };

  return (
    <HL_ProfilePricing id={id} data={data} onRefresh={onRefresh}>
      {renderUI}
    </HL_ProfilePricing>
  );
};

export default ProfileStationsPricing;
